import { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { useSearchHighlight } from '../lib/searchHighlight';
import baseStyles from './HenaketDropdown.module.css';
import styles from './HenaketMultiDropdown.module.css';
// 0.36.265 — Canonical Henaket tag for the chips (was bespoke .chip).
import Tag from './Tag';
import { normalize } from '../utils/format';
// 0.36.278 — Same platform thresholds as the single-select dropdown.
import { getEffectiveInlineThreshold, getSearchThreshold } from '../utils/dropdownThresholds';
import FormCheckboxGroup from './FormCheckboxGroup';

/**
 * HenaketMultiDropdown — multi-select sibling of HenaketDropdown.
 *
 * Architecturally identical to the single-select dropdown — same
 * trigger, panel, search input, highlight model, "one focus
 * indicator at a time" rule, click-outside-to-close, and snap-to-row
 * scroll behavior — with three behavioral differences for multi:
 *
 *   1. `value` is an ARRAY of selected option values.
 *   2. Clicking (or Enter on) an option TOGGLES that value in the
 *      array. The panel does NOT close on selection — the user
 *      typically wants to pick several in a row. Clicking outside
 *      the panel, pressing Escape, Tab, or the trigger again closes.
 *   3. Each option shows a magenta check icon on the right when
 *      selected. Bold weight on selected is retained for parity
 *      with the single-select.
 *
 * Trigger renders selected options as inline chips (canonical Henaket
 * Tag component). 0.36.359 constrained chips to a SINGLE line: only
 * the chips that fit are shown, and a "+N" overflow badge indicates
 * the count that didn't. Chip order is deterministic (option-list
 * order, not click order) so re-renders don't shuffle. Container
 * resize triggers re-measurement via ResizeObserver.
 *
 * Reuses HenaketDropdown.module.css for all shared styles — only the
 * option-row layout (flex with check on right) and the check icon
 * itself live in HenaketMultiDropdown.module.css.
 */

/* 0.36.278 — SEARCH_THRESHOLD removed; admin-tunable via
 * dropdown.search_threshold platform setting. Read via
 * getSearchThreshold() at render time. */

const ChevronDown = () => (
  <svg
    className={baseStyles.chevron}
    viewBox="0 0 10 10"
    width="12"
    height="12"
    aria-hidden="true"
  >
    <path d="M 0 2 L 10 2 L 5 8 Z" fill="currentColor" />
  </svg>
);

const SearchIcon = () => (
  <svg
    /* 0.36.277 — className dropped; icon is centered by its flex
       parent .searchIconWrap now (was absolute-positioned at left:10). */
    viewBox="0 0 16 16"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <circle cx="7" cy="7" r="5" />
    <path d="M 11 11 L 15 15" />
  </svg>
);

// 0.36.171 - `normalize` now imported from ../utils/format (was an
// identical inline duplicate of the one in HenaketDropdown).
export default function HenaketMultiDropdown({
  value = [],
  onChange,
  // 0.45.33 - As HenaketDropdown: colour the search matches, off by default.
  highlightMatches = false,
  highlightColor = null,
  options = [],
  placeholder = '',
  searchPlaceholder = 'Search',
  noMatchesLabel = 'No matches',
  required = false,
  disabled = false,
  name,
  id,
  ariaInvalid = false,
  /* 0.36.278 — Per-call-site escape hatch for the inline-expansion
   * behavior. See HenaketDropdown for rationale. */
  disableAutoExpand = false,
  /* 0.36.278 — Inline-threshold override. See HenaketDropdown — used
   * by the form preview to render citizen behavior from within the
   * admin area; also by the form-editor per-element override. */
  inlineThreshold: inlineThresholdProp,
  /* 0.36.281 — Per-element search-threshold override. Takes
   * precedence over the platform setting from getSearchThreshold(). */
  searchThreshold: searchThresholdProp,
  /* 0.36.656 — Optional server-side data source. Same contract as
   * HenaketDropdown (ship 0.36.655) but for the multi-select case:
   *
   *   {libraryKey:'iso_country_codes', lang:'hy', labelColumn:'code'}
   *
   * Multi-select-specific bits:
   *   - Chip hydration on mount resolves EVERY selected value's label
   *     via /hydrate?keys=<csv> — one call, not one per chip.
   *   - Accumulated-rows cap keeps ALL selected values pinned so
   *     chips never lose their labels, even after paging through
   *     10k+ rows.
   *
   * Fully backward-compatible: when serverSource is null (default),
   * this component behaves identically to before this ship.
   */
  serverSource = null,
}) {
  // 0.36.278 — Inline expansion: when the option count is at or
  // below the EFFECTIVE threshold for the current route (or the
  // explicit override prop when set), render as FormCheckboxGroup
  // instead of the panel UI. value/onChange contracts match
  // (array of selected option values in both).
  const inlineThreshold = inlineThresholdProp !== undefined
    ? inlineThresholdProp
    : getEffectiveInlineThreshold();
  // 0.36.656 — Same rule as HenaketDropdown: serverSource always
  // renders as the panel UI. Row count isn't known synchronously,
  // and library references are conceptually a dropdown regardless
  // of underlying size.
  if (
    !disableAutoExpand &&
    !serverSource &&
    inlineThreshold > 0 &&
    options.length > 0 &&
    options.length <= inlineThreshold
  ) {
    return (
      <FormCheckboxGroup
        value={Array.isArray(value) ? value : []}
        onChange={onChange}
        options={options}
        name={name || id || `checkbox-group-${Math.random().toString(36).slice(2, 8)}`}
        disabled={disabled}
        ariaInvalid={ariaInvalid}
      />
    );
  }
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  // 0.45.33 - Search highlight in the open panel.
  const hlPanelRef = useRef(null);
  useSearchHighlight(hlPanelRef, open ? query : '', highlightMatches);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [optionHighlightActive, setOptionHighlightActive] = useState(true);

  const containerRef = useRef(null);
  // 0.36.167 - same direction + max-height logic as HenaketDropdown
  const [panelDirection, setPanelDirection] = useState('down');
  const [panelMaxHeight, setPanelMaxHeight] = useState(320);
  const searchInputRef = useRef(null);
  const listRef = useRef(null);

  // 0.36.656 — Server-source state. Mirrors HenaketDropdown ship
  // 0.36.655; see that component's comments for the full contract.
  // Guarded by `serverSource` so passing null (the default) means
  // zero API calls fire and the component behaves EXACTLY as before.
  const [serverMode, setServerMode] = useState(null);
  const [serverOptions, setServerOptions] = useState([]);
  const [serverTotal, setServerTotal] = useState(0);
  const [serverPage, setServerPage] = useState(1);
  const [serverHasMore, setServerHasMore] = useState(false);
  const [serverLoading, setServerLoading] = useState(false);
  const [serverError, setServerError] = useState('');
  const searchDebounceRef = useRef(null);
  const ROW_CAP = 1000;
  const SERVER_PER_PAGE = 100;
  const SERVER_THRESHOLD = 300;
  const SEARCH_DEBOUNCE_MS = 300;

  const serverKey = serverSource?.libraryKey || null;
  const serverLang = serverSource?.lang || null;
  const serverLabelCol = serverSource?.labelColumn || null;
  // 0.36.661 — Optional valueColumn. See HenaketDropdown for full doc.
  const serverValueCol = serverSource?.valueColumn || null;
  // 0.36.659 — Multi-column label. See HenaketDropdown for full doc.
  const serverLabelCols = Array.isArray(serverSource?.labelColumns)
    ? serverSource.labelColumns.filter(c => c && String(c).trim())
    : null;
  const serverLabelSep = (serverSource?.labelSeparator != null)
    ? String(serverSource.labelSeparator) : ' ';
  // 0.36.660 — Identity-stable key for the labelColumns array. The
  // useCallback and useEffect dep arrays below reference this string
  // instead of the array itself, so a caller that passes a fresh
  // `serverSource={{...}}` literal on every render doesn't churn
  // `_rowToOption`'s identity and refire all our fetch effects.
  // That churn was the "loading forever" bug in ship 0.36.659.
  const serverLabelColsKey = serverLabelCols
    ? serverLabelCols.join('|') : '';
  // 0.36.658 — Cascade filter parameters. See HenaketDropdown for
  // the full contract. Changes to filterValues (parent selection
  // changes) refetch just like the search query does.
  const serverFilterCol = serverSource?.filterColumn || null;
  const serverFilterValues = Array.isArray(serverSource?.filterValues)
    ? serverSource.filterValues : null;
  const serverFilterKey = serverFilterCol
    ? (serverFilterValues || []).map(String).sort().join(',')
    : '';

  const _rowToOption = useCallback((row, labelColOverride) => {
    // 0.36.661 — Explicit valueColumn takes precedence over the
    // id/code/business_id fallback chain.
    let val;
    if (serverValueCol && row[serverValueCol] != null) {
      val = row[serverValueCol];
    } else {
      val = row.id != null ? row.id : (row.code != null ? row.code : row.business_id);
    }
    // 0.36.659 — Multi-column label. See HenaketDropdown for doc.
    if (serverLabelCols && serverLabelCols.length > 0) {
      const parts = serverLabelCols
        .map(c => (row[c] == null ? '' : String(row[c])))
        .map(s => s.trim())
        .filter(s => s !== '');
      if (parts.length > 0) {
        return { value: val, label: parts.join(serverLabelSep) };
      }
    }
    let label = null;
    if (labelColOverride && row[labelColOverride] != null) {
      label = row[labelColOverride];
    } else if (row.name != null) {
      label = row.name;
    } else if (row.label != null) {
      label = row.label;
    } else {
      label = String(val);
    }
    return { value: val, label: String(label) };
  // 0.36.660 — Stable dep key. See serverLabelColsKey definition.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverLabelColsKey, serverLabelSep, serverValueCol]);

  // /meta on mount → decide mode.
  useEffect(() => {
    if (!serverKey) return undefined;
    let cancelled = false;
    setServerLoading(true);
    setServerError('');
    fetch(`/api/form-libraries/${encodeURIComponent(serverKey)}/meta`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(meta => {
        if (cancelled) return;
        setServerTotal(meta.total_rows || 0);
        setServerMode(
          (meta.total_rows || 0) <= SERVER_THRESHOLD ? 'load-all' : 'paginated',
        );
      })
      .catch(err => {
        if (cancelled) return;
        setServerError(String(err.message || err));
        setServerLoading(false);
      });
    return () => { cancelled = true; };
  }, [serverKey]);

  // load-all mode: fetch every row once.
  useEffect(() => {
    if (!serverKey || serverMode !== 'load-all') return undefined;
    let cancelled = false;
    setServerLoading(true);
    const params = new URLSearchParams();
    if (serverLang) params.set('lang', serverLang);
    // 0.36.658 — Cascade filter forwarded to backend.
    if (serverFilterCol) {
      params.set('filter_column', serverFilterCol);
      params.set('filter_values',
        (serverFilterValues || []).map(String).join(','));
    }
    fetch(`/api/form-libraries/${encodeURIComponent(serverKey)}/rows?${params}`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(data => {
        if (cancelled) return;
        setServerOptions((data.rows || []).map(r => _rowToOption(r, serverLabelCol)));
        setServerHasMore(false);
      })
      .catch(err => {
        if (cancelled) return;
        setServerError(String(err.message || err));
      })
      .finally(() => {
        if (!cancelled) setServerLoading(false);
      });
    return () => { cancelled = true; };
  }, [serverKey, serverMode, serverLang, serverLabelCol,
      serverFilterCol, serverFilterKey, _rowToOption]);

  // paginated: fetch page 1 on mode assignment + on debounced query changes.
  useEffect(() => {
    if (!serverKey || serverMode !== 'paginated') return undefined;
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    let cancelled = false;
    const trimmedQuery = query.trim();
    const doFetch = () => {
      setServerLoading(true);
      setServerError('');
      const params = new URLSearchParams({
        page: '1',
        per_page: String(SERVER_PER_PAGE),
      });
      if (serverLang) params.set('lang', serverLang);
      if (trimmedQuery) params.set('q', trimmedQuery);
      // 0.36.658 — Cascade filter forwarded to backend.
      if (serverFilterCol) {
        params.set('filter_column', serverFilterCol);
        params.set('filter_values',
          (serverFilterValues || []).map(String).join(','));
      }
      fetch(`/api/form-libraries/${encodeURIComponent(serverKey)}/rows?${params}`)
        .then(r => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json();
        })
        .then(data => {
          if (cancelled) return;
          const opts = (data.rows || []).map(r => _rowToOption(r, serverLabelCol));
          setServerOptions(opts);
          setServerPage(1);
          setServerHasMore(opts.length < (data.total || 0));
        })
        .catch(err => {
          if (cancelled) return;
          setServerError(String(err.message || err));
        })
        .finally(() => {
          if (!cancelled) setServerLoading(false);
        });
    };
    if (trimmedQuery) {
      searchDebounceRef.current = setTimeout(doFetch, SEARCH_DEBOUNCE_MS);
    } else {
      doFetch();
    }
    return () => {
      cancelled = true;
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [serverKey, serverMode, serverLang, serverLabelCol, query,
      serverFilterCol, serverFilterKey, _rowToOption]);

  // Infinite scroll for paginated mode. Multi-select-specific twist:
  // when we hit the row cap, all SELECTED values (not just one) stay
  // pinned so their chips retain labels.
  const _handleListScroll = useCallback((e) => {
    if (serverMode !== 'paginated' || !serverHasMore || serverLoading) return;
    const el = e.currentTarget;
    if (!el) return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
    if (!nearBottom) return;
    const nextPage = serverPage + 1;
    setServerLoading(true);
    const params = new URLSearchParams({
      page: String(nextPage),
      per_page: String(SERVER_PER_PAGE),
    });
    if (serverLang) params.set('lang', serverLang);
    const trimmedQuery = query.trim();
    if (trimmedQuery) params.set('q', trimmedQuery);
    // 0.36.658 — Cascade filter forwarded to backend.
    if (serverFilterCol) {
      params.set('filter_column', serverFilterCol);
      params.set('filter_values',
        (serverFilterValues || []).map(String).join(','));
    }
    fetch(`/api/form-libraries/${encodeURIComponent(serverKey)}/rows?${params}`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(data => {
        const newOpts = (data.rows || []).map(r => _rowToOption(r, serverLabelCol));
        setServerOptions(prev => {
          const combined = [...prev, ...newOpts];
          if (combined.length > ROW_CAP) {
            const overflow = combined.length - ROW_CAP;
            // Keep every selected value pinned. Split combined into
            // "would be dropped" and "kept"; pull the selected ones
            // out of the drop pile and prepend them.
            const selectedSetLocal = new Set(value || []);
            const dropped = combined.slice(0, overflow);
            const kept = combined.slice(overflow);
            const pinnedFromDropped = dropped.filter(o => selectedSetLocal.has(o.value));
            return [...pinnedFromDropped, ...kept];
          }
          return combined;
        });
        setServerPage(nextPage);
        const totalLoaded = (serverOptions.length + newOpts.length);
        setServerHasMore(totalLoaded < (data.total || 0));
      })
      .catch(err => {
        setServerError(String(err.message || err));
      })
      .finally(() => setServerLoading(false));
  }, [serverKey, serverMode, serverLang, serverLabelCol, serverPage,
      serverHasMore, serverLoading, serverOptions.length, query, value,
      _rowToOption]);

  // Bulk hydrate: all selected keys whose labels aren't in loaded
  // options. One call resolves every missing label at once.
  useEffect(() => {
    if (!serverKey || !serverMode || !Array.isArray(value) || value.length === 0) {
      return undefined;
    }
    const known = new Set(serverOptions.map(o => o.value));
    const missing = value.filter(v => v != null && v !== '' && !known.has(v));
    if (missing.length === 0) return undefined;
    let cancelled = false;
    const params = new URLSearchParams({
      keys: missing.slice(0, 500).map(String).join(','),
    });
    if (serverLang) params.set('lang', serverLang);
    // 0.36.659 — Multi-column label hydration.
    if (serverLabelCols && serverLabelCols.length > 0) {
      params.set('label_columns', serverLabelCols.join(','));
      params.set('label_separator', serverLabelSep);
    } else if (serverLabelCol) {
      params.set('label_column', serverLabelCol);
    }
    fetch(`/api/form-libraries/${encodeURIComponent(serverKey)}/hydrate?${params}`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(data => {
        if (cancelled) return;
        const items = data.items || [];
        if (items.length === 0) return;
        setServerOptions(prev => {
          const existing = new Set(prev.map(o => o.value));
          const additions = items
            .filter(it => !existing.has(it.key))
            .map(it => ({ value: it.key, label: it.label }));
          if (additions.length === 0) return prev;
          // Prepend so newly-hydrated chips appear at the top of the
          // panel too (matches deterministic-order rule for chips).
          return [...additions, ...prev];
        });
      })
      .catch(() => { /* soft-fail: leave chips with raw IDs */ });
    return () => { cancelled = true; };
  }, [serverKey, serverMode, value, serverLang, serverLabelCol, serverOptions]);

  // 0.36.656 — Effective options: server-managed when serverSource
  // is set, otherwise the caller's `options` prop. All downstream
  // logic (selectedSet interactions, chip render, filtered, toggle)
  // consumes this.
  const effectiveOptions = serverSource ? serverOptions : options;

  // Set membership check used heavily during render — wrap the value
  // array in a Set so .has() lookups are O(1) instead of O(n) per row.
  const selectedSet = useMemo(
    () => new Set(value || []),
    [value],
  );

  const filtered = useMemo(() => {
    // Server-paginated: backend already filtered; return as-is.
    if (serverSource && serverMode === 'paginated') return effectiveOptions;
    if (!query.trim()) return effectiveOptions;
    const q = normalize(query);
    return effectiveOptions.filter(o => normalize(o.label).includes(q));
  }, [effectiveOptions, query, serverSource, serverMode]);

  const openPanel = useCallback(() => {
    if (disabled) return;
    setQuery('');
    // Start highlight at top — there's no single "selected" option
    // to seed from like in the single-select case.
    setHighlightIndex(0);
    setOptionHighlightActive(true);
    setOpen(true);
  }, [disabled]);

  const closePanel = useCallback(() => {
    setOpen(false);
    setQuery('');
  }, []);

  // Toggle: add if missing, remove if present. Panel stays open.
  // The new array is built by filtering OPTIONS (not the current value)
  // through the resulting set so the value array's order always
  // matches the order options were originally declared in — handy
  // for displaying the trigger label deterministically.
  const toggle = useCallback((v) => {
    const next = new Set(value || []);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    // 0.36.656 — Use effectiveOptions so server-managed options
    // work the same way. In server mode not every currently-loaded
    // option is present (paged), so we also union with any missing
    // values so a previously-selected value that's been paged out
    // still stays in the result.
    const seenInOpts = new Set(effectiveOptions.map(o => o.value));
    const fromOpts = effectiveOptions
      .filter(o => next.has(o.value))
      .map(o => o.value);
    const carryOver = [...next].filter(v0 => !seenInOpts.has(v0));
    onChange?.([...fromOpts, ...carryOver]);
  }, [value, effectiveOptions, onChange]);

  // Click-outside closes

  // 0.36.167 - measure space, decide direction + max-height when opening
  const _measurePanel = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const viewportH = window.innerHeight || document.documentElement.clientHeight;
    const BUFFER = 8;
    const MAX = 320;
    const MIN = 120;
    const spaceBelow = viewportH - rect.bottom - BUFFER;
    const spaceAbove = rect.top - BUFFER;
    if (spaceBelow >= MAX) {
      setPanelDirection('down');
      setPanelMaxHeight(MAX);
    } else if (spaceAbove > spaceBelow) {
      setPanelDirection('up');
      setPanelMaxHeight(Math.min(MAX, Math.max(MIN, spaceAbove)));
    } else {
      setPanelDirection('down');
      setPanelMaxHeight(Math.max(MIN, spaceBelow));
    }
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    _measurePanel();
    const onResize = () => _measurePanel();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [open, _measurePanel]);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        closePanel();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, closePanel]);

  // Scroll highlighted into view. Same approach as HenaketDropdown
  // since 0.35.43 — scrollIntoView({block: 'nearest'}) leveraging
  // the panel's scroll-padding-top: 45px to keep the option clear
  // of the sticky search bar.
  useEffect(() => {
    if (!open || !listRef.current) return undefined;
    const raf = requestAnimationFrame(() => {
      const list = listRef.current;
      if (!list) return;
      const el = list.children[highlightIndex];
      if (!el) return;
      el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
    return () => cancelAnimationFrame(raf);
  }, [highlightIndex, open]);

  useEffect(() => {
    if (highlightIndex >= filtered.length) {
      setHighlightIndex(0);
    }
  }, [filtered.length, highlightIndex]);

  // -- Focus / highlight transitions (mirror single-select) --
  const handleSearchFocus = () => setOptionHighlightActive(false);

  const handleOptionMouseEnter = (idx) => {
    if (document.activeElement === searchInputRef.current) {
      searchInputRef.current.blur();
    }
    setOptionHighlightActive(true);
    setHighlightIndex(idx);
  };

  const moveHighlight = (direction) => {
    setOptionHighlightActive(true);
    setHighlightIndex(i =>
      direction === 'down'
        ? Math.min(i + 1, Math.max(filtered.length - 1, 0))
        : Math.max(i - 1, 0)
    );
  };

  const commitHighlighted = () => {
    const opt = filtered[highlightIndex];
    if (opt) toggle(opt.value);
  };

  const handleTriggerKeyDown = (e) => {
    if (disabled) return;
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        openPanel();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveHighlight('down');
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveHighlight('up');
    } else if (e.key === 'Enter' || e.key === ' ') {
      // In multi-select, Enter on the trigger TOGGLES the highlighted
      // option. Panel stays open so the user can keep selecting.
      e.preventDefault();
      commitHighlighted();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closePanel();
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveHighlight('down');
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveHighlight('up');
    } else if (e.key === 'Enter') {
      e.preventDefault();
      commitHighlighted();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closePanel();
    } else if (e.key === 'Tab') {
      closePanel();
    }
  };

  // Selected option objects in option-list order — used to render
  // chips in the trigger. We iterate options (not value) so chip
  // order is deterministic regardless of how the user selected them.
  // 0.36.656 — Consumes `effectiveOptions` so server-hydrated chips
  // show their real labels instead of raw IDs.
  const triggerSelectedOpts = useMemo(
    () => effectiveOptions.filter(o => selectedSet.has(o.value)),
    [effectiveOptions, selectedSet],
  );

  // 0.36.359 — Trigger chip overflow. Previously chips wrapped onto
  // multiple lines via flex-wrap: wrap, which made the trigger grow
  // arbitrarily tall for large selections (e.g. 249 selected
  // countries). New behavior: chips stay on ONE line, and a "+N"
  // overflow badge indicates the count that didn't fit.
  //
  // Implementation:
  //   - Visible layer renders `triggerSelectedOpts.slice(0,
  //     visibleChipCount)` + the "+N" badge.
  //   - Measurement layer (position: absolute, visibility: hidden)
  //     always renders ALL chips so we can measure their natural
  //     widths without affecting layout.
  //   - useLayoutEffect measures container width, iterates chip
  //     widths from the measurement layer, and computes how many fit
  //     when a "+N" badge is also reserved (reserved width comes
  //     from a sample badge in the measurement layer).
  //   - ResizeObserver on the wrapper triggers re-measurement on
  //     any container resize (window resize, sibling changes).
  const chipsWrapperRef = useRef(null);
  const chipsMeasureRef = useRef(null);
  const [visibleChipCount, setVisibleChipCount] = useState(
    triggerSelectedOpts.length
  );
  const [chipsWrapperWidth, setChipsWrapperWidth] = useState(0);

  // ResizeObserver: track container width so re-measurement happens
  // on window resize, sidebar toggle, or any layout change that
  // affects the trigger's inner width.
  useEffect(() => {
    const wrapper = chipsWrapperRef.current;
    if (!wrapper) return;
    // Initial read — offsetWidth is 0 during first paint of a
    // freshly-mounted trigger; the observer will fire immediately
    // after and set the real width.
    setChipsWrapperWidth(wrapper.offsetWidth);
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = Math.round(entry.contentRect.width);
        setChipsWrapperWidth(prev => (prev === w ? prev : w));
      }
    });
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, []);

  // Measurement pass. Runs after every render whose deps changed —
  // selection change or container width change. Reads chip widths
  // from the measurement layer (which always renders every chip),
  // computes the largest N such that N chips + optional "+M" badge
  // fit within the container width, and sets visibleChipCount.
  useLayoutEffect(() => {
    const measure = chipsMeasureRef.current;
    if (!measure) return;
    if (chipsWrapperWidth === 0) return;
    if (triggerSelectedOpts.length === 0) {
      if (visibleChipCount !== 0) setVisibleChipCount(0);
      return;
    }

    const chipEls = measure.querySelectorAll('[data-measure-chip]');
    const badgeEl = measure.querySelector('[data-measure-badge]');
    const badgeWidth = badgeEl ? badgeEl.offsetWidth : 32;
    const gap = 6; // matches the .chips CSS gap

    let cumulative = 0;
    let fit = 0;
    // 0.36.361 — Subtract 1px from the container width as a safety
    // buffer against sub-pixel rounding. Widths from offsetWidth are
    // integers but layout can use fractional pixels; without this,
    // a chip+badge combo that measures at exactly the container's
    // rounded width can still overflow the `overflow: hidden` and
    // clip the "+N".
    const availableWidth = chipsWrapperWidth - 1;
    for (let i = 0; i < chipEls.length; i++) {
      const chipWidth = chipEls[i].offsetWidth;
      const isLast = i === chipEls.length - 1;
      // If i is NOT the last chip, we need to reserve room for a
      // "+M" overflow badge in case chip i+1 doesn't fit.
      const reservedBadge = isLast ? 0 : (badgeWidth + gap);
      const gapBefore = i > 0 ? gap : 0;
      if (cumulative + gapBefore + chipWidth + reservedBadge > availableWidth) {
        break;
      }
      cumulative += gapBefore + chipWidth;
      fit++;
    }

    // 0.36.360 — Clamp `fit` to at least 1 when we have chips AND
    // width but the greedy loop above returned 0 (happens in extremely
    // narrow triggers where one chip + badge doesn't fit). Showing 1
    // clipped chip + badge reads better than showing only "+N".
    if (fit === 0 && chipEls.length > 0) fit = 1;

    if (fit !== visibleChipCount) setVisibleChipCount(fit);
  }, [triggerSelectedOpts, chipsWrapperWidth, visibleChipCount]);

  const overflowCount = Math.max(
    0, triggerSelectedOpts.length - visibleChipCount
  );

  const showingPlaceholder = !value || value.length === 0;

  const triggerClass = [
    baseStyles.trigger,
    styles.trigger,
    open && baseStyles.triggerOpen,
    showingPlaceholder && baseStyles.triggerPlaceholder,
    disabled && styles.triggerDisabled,
  ].filter(Boolean).join(' ');

  return (
    <div className={baseStyles.container} ref={containerRef}>
      {/* Trigger as a <div role="combobox" tabIndex={0}>. Real
        * <button>s for chip-remove × can't live inside a real
        * <button> trigger (HTML forbids interactive nesting), so the
        * trigger is a div with manual keyboard handling.
        *
        * 0.35.45 — chip × buttons set tabIndex={-1} so Tab doesn't
        * land on them. Keyboard users navigate trigger → next page
        * element directly, then deselect via the dropdown options
        * (the standard multi-select pattern; react-select et al.
        * work this way). Mouse users still click × to remove. */}
      <div
        id={id}
        className={triggerClass}
        role="combobox"
        tabIndex={disabled ? -1 : 0}
        onClick={(e) => {
          if (disabled) return;
          // Defensive: if the click originated on a chip remove
          // button (the × buttons inside the chips), do nothing —
          // the button's own onClick already handled the removal,
          // and we don't want to focus-the-trigger or toggle-the-
          // panel as a side effect. The button's own onClick has
          // e.stopPropagation(), but in practice React's synthetic
          // event bubbling + focus-on-click can still let the
          // trigger's handler run depending on browser timing.
          // This data-attribute check is the reliable gate.
          if (e.target.closest('[data-chip-remove]')) return;
          e.currentTarget.focus();
          if (open) closePanel();
          else openPanel();
        }}
        onKeyDown={handleTriggerKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={ariaInvalid}
        aria-required={required}
        aria-disabled={disabled}
      >
        {/* 0.36.360 — Wrapper ALWAYS renders (chipsWrapperRef must be
            attached before the ResizeObserver setup runs, otherwise
            the observer never attaches when the component starts
            empty and later gains a selection). The placeholder /
            chip content is a child that swaps based on selection. */}
        <div className={styles.chipsWrapper} ref={chipsWrapperRef}>
          {showingPlaceholder ? (
            <span className={baseStyles.triggerLabel}>{placeholder || ''}</span>
          ) : (
            <>
              {/* Measurement layer — off-screen, no interaction.
                  Renders every chip + a representative "+N" badge
                  so the layout effect can measure natural widths.
                  0.36.361 — Measurement Tags MUST render with the
                  same `closable` prop as the visible layer,
                  otherwise the × button is missing and each Tag
                  measures narrower than it actually renders. That
                  under-measures widths, causes the visible "+N" to
                  overflow the container's `overflow: hidden` and
                  vanish. The measurement layer is aria-hidden and
                  pointer-events: none, so the × onClose is
                  unreachable — the prop only serves to reserve the
                  correct pixel width. data-measure-chip is passed
                  directly on the Tag (Tag spreads ...rest onto its
                  outer span), so querySelectorAll finds the Tags
                  themselves — no wrapper divs that would inflate
                  the measurement. */}
              <div className={styles.chipsMeasure} ref={chipsMeasureRef} aria-hidden="true">
                {triggerSelectedOpts.map(opt => (
                  <Tag
                    key={opt.value}
                    closable
                    data-measure-chip
                    style={{ maxWidth: 200 }}
                  >
                    {/* 0.37.59 — When a caller passes opt.chipLabel,
                        the trigger chip uses it in place of opt.label.
                        Lets the panel option list show a fuller
                        "id (Label)" display while the trigger chip
                        stays compact (e.g. just the element_id).
                        Fully backward compatible — options without
                        chipLabel render exactly as before. */}
                    {opt.chipLabel != null ? opt.chipLabel : opt.label}
                  </Tag>
                ))}
                <span data-measure-badge className={styles.overflowBadge}>
                  +{triggerSelectedOpts.length || 1}
                </span>
              </div>
              {/* Visible layer — chips that fit on one line + "+N"
                  for any that don't. */}
              <div className={styles.chips}>
                {/* 0.36.265 — Chips render via the canonical Henaket
                    <Tag> component (filled variant, closable). */}
                {triggerSelectedOpts.slice(0, visibleChipCount).map(opt => {
                  const chipText = opt.chipLabel != null ? opt.chipLabel : opt.label;
                  return (
                    <Tag
                      key={opt.value}
                      closable
                      closeAriaLabel={`Remove ${chipText}`}
                      onClose={() => toggle(opt.value)}
                      data-chip-remove
                      style={{ maxWidth: 200 }}
                      title={chipText}
                    >
                      {chipText}
                    </Tag>
                  );
                })}
                {overflowCount > 0 && (
                  <span className={styles.overflowBadge}
                        title={`${overflowCount} more selected`}>
                    +{overflowCount}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
        {/* Hidden form-field name shim — preserves the API contract
          * (caller can read submitted values by name) without using
          * a real input that would mess with our layout. */}
        {name && (
          <input
            type="hidden"
            name={name}
            value={Array.isArray(value) ? value.join(',') : ''}
            readOnly
          />
        )}
        <ChevronDown />
      </div>

      {open && (
        <div
          ref={hlPanelRef}
          className={panelDirection === 'up' ? `${baseStyles.panel} ${baseStyles.panelUp}` : baseStyles.panel}
          style={{ maxHeight: `${panelMaxHeight}px`,
                   ...(highlightColor ? { '--search-highlight-color': highlightColor } : null) }}
          role="dialog"
        >
          {/* 0.36.277 — Same structural overhaul as HenaketDropdown:
              search is a self-contained bordered box; listBox is a
              separate self-contained bordered scroll container; 2 px
              flex `gap` from the panel keeps them visually
              separated. Icon affix now on the right with a 1 px
              vertical separator before it. */}
          {/* 0.36.278 / 0.36.281 — Same precedence as HenaketDropdown:
              per-element prop > platform setting. */}
          {(() => {
            // 0.36.656 — In paginated server mode, search input is
            // always visible: it's the only way to find rows in a
            // 10k library.
            if (serverSource && serverMode === 'paginated') return true;
            const t = searchThresholdProp !== undefined
              ? searchThresholdProp
              : getSearchThreshold();
            return t > 0 && effectiveOptions.length > t;
          })() && (
            <div className={baseStyles.searchBox}>
              <input
                ref={searchInputRef}
                type="text"
                className={baseStyles.searchInput}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={handleSearchFocus}
                onKeyDown={handleSearchKeyDown}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                autoComplete="off"
              />
              <div className={baseStyles.searchSeparator} />
              <div className={baseStyles.searchIconWrap}>
                <SearchIcon />
              </div>
            </div>
          )}

          {/* 0.36.656 — Server-source paginated mode wires the scroll
              handler onto the listBox for infinite-append. Loading +
              error indicators sit at the top / bottom of the list. */}
          <div
            className={baseStyles.listBox}
            onScroll={serverSource ? _handleListScroll : undefined}
          >
            {serverSource && serverError && (
              <div className={baseStyles.empty} style={{ color: 'var(--color-error)' }}>
                {serverError}
              </div>
            )}
            {filtered.length === 0 && !serverLoading ? (
              <div className={baseStyles.empty}>{noMatchesLabel}</div>
            ) : (
              <ul
                ref={listRef}
                className={baseStyles.list}
                role="listbox"
                aria-multiselectable="true"
              >
              {filtered.map((opt, idx) => {
                const isSelected = selectedSet.has(opt.value);
                const isHighlighted = idx === highlightIndex;
                const showBorder = optionHighlightActive && isHighlighted;
                const optionClass = [
                  baseStyles.option,
                  styles.optionRow,
                  isSelected && baseStyles.optionSelected,
                  showBorder && baseStyles.optionHighlighted,
                ].filter(Boolean).join(' ');
                return (
                  <li
                    key={opt.value}
                    className={optionClass}
                    role="option"
                    aria-selected={isSelected}
                    onMouseEnter={() => handleOptionMouseEnter(idx)}
                    /* 0.36.30 — Same fix as HenaketDropdown: stop the
                     * native mousedown from stealing focus away from
                     * the trigger div. Multi-select keeps the panel
                     * open across picks, so the focus problem here
                     * compounds — every click was nudging focus
                     * onto the LI (then nowhere, since LI isn't
                     * focusable) and the trigger ring went away. */
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => toggle(opt.value)}
                  >
                    {/* Native browser <input type="checkbox"> — same
                      * control as FormCheckboxGroup. The browser renders
                      * its rounded corners, default size, accent-color
                      * fill. `readOnly` + `pointer-events: none` so all
                      * interaction goes through the parent <li>'s
                      * onClick → toggle().
                      *
                      * 0.35.44 — focus indicator (.optionCheckboxActive)
                      * applied to the INPUT itself, not the wrap, so the
                      * outline follows the input's global 4px
                      * border-radius and looks identical to a standalone
                      * checkbox's focus ring. */}
                    <span className={styles.optionCheckboxWrap}>
                      <input
                        type="checkbox"
                        className={`${styles.optionCheckbox} ${showBorder ? styles.optionCheckboxActive : ''}`}
                        checked={isSelected}
                        readOnly
                        tabIndex={-1}
                        aria-hidden="true"
                      />
                    </span>
                    <span className={styles.optionLabel}>{opt.label}</span>
                  </li>
                );
              })}
              {/* 0.36.656 — Loading strip at the bottom while the
                  next page is in flight. */}
              {serverSource && serverMode === 'paginated' && serverLoading && (
                <li className={baseStyles.empty} style={{ padding: 8, opacity: 0.7 }}>
                  Loading…
                </li>
              )}
            </ul>
          )}
          </div>
        </div>
      )}
    </div>
  );
}
